import { UnifiedLedgerType, IRowSource } from '@opengov/reporting-types';
import { produce } from 'immer';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

import { IGroupBy, IRowEditorSetupFormValues, RowGroupOptions } from '../../../../types/MultiDatasetTypes';
import {
  findBestGroupByForShow,
  getInitialFormValues,
  swapArrayElementsWithMutation,
  validateForm
} from './useSetupForm-helper';

export const GROUPING_LIMIT = 7;

export interface IUseSetupForm {
  formValues: IRowEditorSetupFormValues;
  isValidForm: boolean;
  isDirty: boolean;
  setShow(value: UnifiedLedgerType): void;
  setGroupBy(value: IGroupBy, index: number): void;
  setGroupName(value: string): void;
  addGroupBy(): void;
  removeGroupBy(fieldIndex: number): void;
  moveUp(fieldIndex: number): void;
  moveDown(fieldIndex: number): void;
}

export const useSetupForm = (
  rowGroups: IRowSource[],
  rowGroupIndex: number,
  options: RowGroupOptions,
  setEditSessionDirtyState: (dirtyState: boolean) => void
): IUseSetupForm => {
  const initialFormValues = getInitialFormValues(rowGroups, rowGroupIndex, options);
  const [formValues, setFormValues] = useState<IRowEditorSetupFormValues>(() => initialFormValues);
  const isValidForm = validateForm(formValues);
  const isDirty = !isEqual(formValues, initialFormValues);

  const setFormValuesImmutable = (callback: (draft: IRowEditorSetupFormValues) => IRowEditorSetupFormValues) => {
    setFormValues(produce(formValues, callback));
  };

  useEffect(() => {
    const isDirty = !isEqual(formValues, initialFormValues);
    setEditSessionDirtyState(isDirty);
  }, [initialFormValues, formValues, setEditSessionDirtyState]);

  const setShow = (value: UnifiedLedgerType) => {
    if (options[value]) {
      setFormValues({
        show: value,
        groupName: options[value].label,
        groupBy: [findBestGroupByForShow(options[value])]
      });
    }
  };

  const addGroupBy = () => {
    setFormValuesImmutable((draft) => {
      if (draft.groupBy.length < GROUPING_LIMIT) {
        const newGroupByField =
          draft.groupBy.length < 1
            ? findBestGroupByForShow(options[draft.show])
            : draft.groupBy[draft.groupBy.length - 1];
        draft.groupBy.push(newGroupByField);
      }
      return draft;
    });
  };

  const removeGroupBy = (fieldIndex: number) => {
    setFormValuesImmutable((draft) => {
      draft.groupBy = draft.groupBy.filter((_, index) => index !== fieldIndex);
      return draft;
    });
  };

  const moveDown = (fieldIndex: number) => {
    setFormValuesImmutable((draft) => {
      try {
        if (fieldIndex <= draft.groupBy.length - 2)
          swapArrayElementsWithMutation(draft.groupBy, fieldIndex, fieldIndex + 1);
      } catch (error) {
        console.error(error);
      }
      return draft;
    });
  };

  const moveUp = (fieldIndex: number) => {
    setFormValuesImmutable((draft) => {
      try {
        if (fieldIndex > 0) swapArrayElementsWithMutation(draft.groupBy, fieldIndex, fieldIndex - 1);
      } catch (error) {
        console.error(error);
      }
      return draft;
    });
  };

  const setGroupBy = (value: IGroupBy, index: number) => {
    setFormValuesImmutable((draft) => {
      draft.groupBy[index] = value;
      return draft;
    });
  };

  const setGroupName = (value) => {
    setFormValues((values) => ({
      ...values,
      groupName: value
    }));
  };

  return {
    formValues,
    isValidForm,
    isDirty,
    setShow,
    setGroupBy,
    setGroupName,
    addGroupBy,
    removeGroupBy,
    moveUp,
    moveDown
  };
};
